import type { UserProfile, WhatsAppVerificationStart } from '@/lib/api';
import type { ConsentState } from '@/types/onboarding';
import {
  isValidBrazilianMobile,
  normalizeWhatsAppPhoneForSubmit,
} from '@/components/onboarding/platformOnboarding';
import { isValidCpf, normalizeCpf } from '@/lib/document';

// The Validação step's behavior, pure (VER-388). One reducer rather than a pile of
// useState: the CPF, phone, code and checkbox fields reset in lockstep across the
// OTP transitions, and the gate below is unit-testable without mounting anything.

export type ValidationPending =
  | 'idle'
  | 'otp_start'
  | 'otp_resend'
  | 'otp_confirm'
  | 'continuing';

export type ValidationErrorField = 'cpf' | 'phone' | 'code' | 'form';

export type ValidationDraft = {
  cpf: string;
  phone: string;
  code: string;
  verification: WhatsAppVerificationStart | null;
  requiredConsent: boolean;
  pending: ValidationPending;
  error: { field: ValidationErrorField; message: string } | null;
};

export type ValidationAction =
  | { type: 'cpf_changed'; value: string }
  | { type: 'phone_changed'; value: string }
  | { type: 'phone_blurred'; value: string }
  | { type: 'code_changed'; value: string }
  | { type: 'verification_started'; verification: WhatsAppVerificationStart; phone: string }
  | { type: 'verification_expired' }
  | { type: 'verification_reset' }
  | { type: 'required_consent_toggled'; checked: boolean }
  | { type: 'pending'; value: ValidationPending }
  | { type: 'failed'; field: ValidationErrorField; message: string }
  | { type: 'settled' };

export const initialValidationDraft: ValidationDraft = {
  cpf: '',
  phone: '',
  code: '',
  verification: null,
  requiredConsent: false,
  pending: 'idle',
  error: null,
};

export function validationReducer(state: ValidationDraft, action: ValidationAction): ValidationDraft {
  switch (action.type) {
    case 'cpf_changed':
      return { ...state, cpf: action.value, error: null };
    case 'phone_changed':
      return { ...state, phone: action.value, error: null };
    case 'phone_blurred':
      return { ...state, phone: action.value };
    case 'code_changed':
      return { ...state, code: action.value.replace(/\D/g, '').slice(0, 6), error: null };
    case 'verification_started':
      return {
        ...state,
        verification: action.verification,
        phone: action.phone,
        code: '',
        pending: 'idle',
        error: null,
      };
    case 'verification_expired':
      return {
        ...state,
        verification: null,
        code: '',
        error: { field: 'code', message: 'Código expirado. Solicite um novo código.' },
      };
    case 'verification_reset':
      // Also the success path after a confirmed code — it must settle pending,
      // or the screen stays busy (disabled checkboxes) forever.
      return { ...state, verification: null, code: '', pending: 'idle', error: null };
    case 'required_consent_toggled':
      return { ...state, requiredConsent: action.checked, error: null };
    case 'pending':
      return { ...state, pending: action.value, error: null };
    case 'failed':
      return { ...state, pending: 'idle', error: { field: action.field, message: action.message } };
    case 'settled':
      return { ...state, pending: 'idle' };
  }
}

export type ValidationProfileFacts = Pick<
  UserProfile,
  'hasCpf' | 'whatsAppVerificationRequired' | 'hasWhatsAppUserId'
>;

export type ValidationBlockReason = 'cpf' | 'whatsapp' | 'consent' | null;

/**
 * The first unmet requirement, or null when "Continuar" is live. WhatsApp gates
 * only while the environment requires it (whatsAppVerificationRequired is the
 * ops kill-switch — staging runs with it off, and prod turns it off if Meta
 * delivery degrades; the block still renders, it just stops gating).
 */
export function validationBlockReason(
  draft: ValidationDraft,
  profile: ValidationProfileFacts,
  requiredConsentGranted: boolean,
): ValidationBlockReason {
  if (!profile.hasCpf && !isValidCpf(normalizeCpf(draft.cpf))) return 'cpf';
  if (profile.whatsAppVerificationRequired && !profile.hasWhatsAppUserId) return 'whatsapp';
  if (!requiredConsentGranted && !draft.requiredConsent) return 'consent';
  return null;
}

export function requiredConsentGranted(consents: ConsentState | null | undefined): boolean {
  // A grant at an outdated version reads as NOT satisfied: the checkbox must
  // come back so the renewal is collected at the current version. Completion
  // itself stays lenient (the gate does not require the current version), so
  // this re-asks without stranding anyone mid-flow.
  return consents?.required.granted === true && consents.required.needsRenewal !== true;
}

/**
 * Pre-send validation for the phone, each failure with its own message — on a
 * BLOCKING step, a number that can never receive the code is a dead end with no
 * feedback (the old check validated emptiness only, so "+551" was submitted and
 * the user waited forever).
 */
export function phoneSubmitError(phone: string): string | null {
  const normalized = normalizeWhatsAppPhoneForSubmit(phone);
  if (!normalized) return 'Informe seu número de WhatsApp.';
  if (isValidBrazilianMobile(normalized)) return null;

  // Right shape, wrong DDD — name the actual problem.
  const digits = normalized.replace(/\D/g, '');
  const shapedLikeMobile = digits.length === 13 && digits.startsWith('55') && digits[4] === '9';
  return shapedLikeMobile
    ? 'Esse DDD não existe. Confira o número.'
    : 'Informe um celular válido, com DDD e 9 dígitos.';
}
